import React, { useState, useEffect, useCallback } from 'react';
import { pmsAPI } from '../../../services';
import { Target, Plus, CheckCircle, Clock, AlertCircle, Star, X } from 'lucide-react';

const CATEGORIES = ['technical','leadership','learning','quality','performance','sales','process','hr','other'];
const CAT_LABELS = { technical:'Technical', leadership:'Leadership', learning:'Learning', quality:'Quality', performance:'Performance', sales:'Sales', process:'Process', hr:'HR', other:'Other' };
const STATUS_COLORS = { draft:'bg-gray-100 text-gray-600', discussion:'bg-blue-100 text-blue-700', pending_approval:'bg-yellow-100 text-yellow-800', approved:'bg-green-100 text-green-700', resubmit_requested:'bg-red-100 text-red-700' };
const STATUS_LABELS = { draft:'Draft', discussion:'In Discussion', pending_approval:'Pending Approval', approved:'Approved', resubmit_requested:'Resubmit Required' };
const YEAR = new Date().getFullYear();

const MyGoalsPage = () => {
  const [goals, setGoals] = useState([]);
  const [totalWeight, setTotalWeight] = useState(0);
  const [kpiScore, setKpiScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editGoal, setEditGoal] = useState(null);
  const [showRating, setShowRating] = useState(null);
  const [form, setForm] = useState({ title:'', description:'', category:'other', weightage:'', employeeNote:'' });
  const [selfRating, setSelfRating] = useState({ selfRating:'', selfComment:'' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type='success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await pmsAPI.getMyGoals(YEAR);
      setGoals(res?.data?.goals || []);
      setTotalWeight(res?.data?.totalWeight || 0);
      setKpiScore(res?.data?.kpiScore);
    } catch { showToast('Failed to load goals', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm({ title:'', description:'', category:'other', weightage:'', employeeNote:'' }); setEditGoal(null); setShowForm(true); };
  const openEdit = (g) => { setForm({ title:g.title, description:g.description||'', category:g.category, weightage:g.weightage, employeeNote:g.employeeNote||'' }); setEditGoal(g._id); setShowForm(true); };

  const handleSave = async () => {
    if (!form.title.trim() || !form.weightage) return showToast('Title and weightage required', 'error');
    const newTotal = totalWeight - (editGoal ? goals.find(g=>g._id===editGoal)?.weightage||0 : 0) + parseFloat(form.weightage);
    if (newTotal > 100) return showToast('Total weightage cannot exceed 100%', 'error');
    try {
      setSaving(true);
      if (editGoal) await pmsAPI.updateGoal(editGoal, { ...form, weightage: parseFloat(form.weightage) });
      else await pmsAPI.createGoal({ ...form, weightage: parseFloat(form.weightage), year: YEAR });
      showToast(editGoal ? 'Goal updated' : 'Goal created');
      setShowForm(false); load();
    } catch { showToast('Save failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleSubmit = async (id, action) => {
    try { await pmsAPI.submitGoal(id, action); showToast('Status updated'); load(); }
    catch { showToast('Failed', 'error'); }
  };

  const handleSelfRating = async () => {
    if (!selfRating.selfRating) return showToast('Please select a rating', 'error');
    try {
      setSaving(true);
      await pmsAPI.submitSelfRating(showRating._id, { selfRating: parseInt(selfRating.selfRating), selfComment: selfRating.selfComment });
      showToast('Self rating submitted'); setShowRating(null); load();
    } catch { showToast('Failed', 'error'); }
    finally { setSaving(false); }
  };

  const weightLeft = 100 - totalWeight;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white text-sm ${toast.type==='error'?'bg-red-500':'bg-green-500'}`}>{toast.msg}</div>}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Target className="w-6 h-6 text-blue-600"/>My Goals — {YEAR}</h1>
          <p className="text-sm text-gray-500 mt-1">Set, track and manage your performance goals</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"><Plus className="w-4 h-4"/>New Goal</button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-3"><Target className="w-8 h-8 text-blue-500"/><div><p className="text-xs text-gray-500">Total Goals</p><p className="text-xl font-bold text-blue-700">{goals.length}</p></div></div>
        <div className={`rounded-xl p-4 flex items-center gap-3 ${totalWeight===100?'bg-green-50':'bg-yellow-50'}`}>
          <CheckCircle className={`w-8 h-8 ${totalWeight===100?'text-green-500':'text-yellow-500'}`}/>
          <div><p className="text-xs text-gray-500">Weightage Used</p><p className={`text-xl font-bold ${totalWeight===100?'text-green-700':'text-yellow-700'}`}>{totalWeight}%</p></div>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 flex items-center gap-3"><Star className="w-8 h-8 text-purple-500"/><div><p className="text-xs text-gray-500">KPI Score</p><p className="text-xl font-bold text-purple-700">{kpiScore ? `${parseFloat(kpiScore).toFixed(2)}/5` : '—'}</p></div></div>
      </div>

      {totalWeight > 0 && totalWeight < 100 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800">⚠️ Total weightage is {totalWeight}%. You have {weightLeft}% remaining to allocate.</div>
      )}
      {totalWeight > 100 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">⛔ Total weightage exceeds 100%! Please adjust your goals.</div>
      )}

      {/* Goals list */}
      {loading ? <div className="text-center py-12 text-gray-400">Loading...</div>
      : goals.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200 text-gray-400">
          <Target className="w-10 h-10 mb-2 mx-auto opacity-30"/>No goals yet. Add your first goal for {YEAR}.
        </div>
      ) : (
        <div className="space-y-3">
          {goals.map(g => (
            <div key={g._id} className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="font-semibold text-gray-900">{g.title}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[g.status]}`}>{STATUS_LABELS[g.status]}</span>
                    {g.goalType === 'manager_assigned' && <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">Manager Assigned</span>}
                  </div>
                  <p className="text-xs text-gray-500">{CAT_LABELS[g.category]} · {g.weightage}% weight</p>
                  {g.description && <p className="text-sm text-gray-600 mt-1">{g.description}</p>}
                  {g.managerNote && <p className="text-xs text-blue-700 mt-1 italic">Manager: {g.managerNote}</p>}
                  <div className="flex gap-4 mt-2 text-xs text-gray-500">
                    {g.selfRating && <span>Self: {g.selfRating}/5 ⭐</span>}
                    {g.managerRating && <span>Manager: {g.managerRating}/5 ⭐</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1 items-end flex-shrink-0">
                  {['draft','resubmit_requested'].includes(g.status) && g.goalType !== 'manager_assigned' && (
                    <>
                      <button onClick={() => openEdit(g)} className="text-xs text-blue-600 hover:underline">Edit</button>
                      <button onClick={() => handleSubmit(g._id, 'discussion')} className="text-xs text-yellow-700 hover:underline">Send for Discussion</button>
                      <button onClick={() => handleSubmit(g._id, 'pending_approval')} className="text-xs text-green-700 hover:underline">Submit for Approval</button>
                    </>
                  )}
                  {g.status === 'discussion' && (
                    <button onClick={() => handleSubmit(g._id, 'pending_approval')} className="text-xs text-green-700 hover:underline">Submit for Approval</button>
                  )}
                  {g.status === 'approved' && !g.selfRating && (
                    <button onClick={() => { setShowRating(g); setSelfRating({ selfRating:'', selfComment:'' }); }} className="text-xs text-purple-600 hover:underline">Self Rate</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">{editGoal?'Edit Goal':'New Goal'}</h2>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Goal title"/></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={form.category} onChange={e=>setForm(f=>({...f,category:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    {CATEGORIES.map(c=><option key={c} value={c}>{CAT_LABELS[c]}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Weightage (%) *</label>
                  <input type="number" min="1" max="100" value={form.weightage} onChange={e=>setForm(f=>({...f,weightage:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="e.g. 30"/></div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea value={form.employeeNote} onChange={e=>setForm(f=>({...f,employeeNote:e.target.value}))} rows={1} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Any context or notes..."/></div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">{saving?'Saving...':'Save Goal'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Self Rating Modal */}
      {showRating && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900">Self Assessment — {showRating.title}</h2>
              <button onClick={() => setShowRating(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5"/></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Rating (1–5) *</label>
                <div className="flex gap-2">
                  {[1,2,3,4,5].map(r=>(
                    <button key={r} onClick={() => setSelfRating(s=>({...s,selfRating:r}))} className={`w-10 h-10 rounded-full font-bold text-sm border-2 transition-all ${selfRating.selfRating===r?'border-blue-500 bg-blue-500 text-white':'border-gray-200 text-gray-700 hover:border-blue-300'}`}>{r}</button>
                  ))}
                </div>
              </div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
                <textarea value={selfRating.selfComment} onChange={e=>setSelfRating(s=>({...s,selfComment:e.target.value}))} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Describe your achievements for this goal..."/></div>
              <div className="flex gap-3">
                <button onClick={() => setShowRating(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button onClick={handleSelfRating} disabled={saving} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-60">{saving?'Submitting...':'Submit Rating'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyGoalsPage;
